import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, UploadCloud, Loader2, Save, Sparkles } from "lucide-react";
import { DEFAULT_WATCH } from "@/components/dash/AlphalinerWatch";
import { DEFAULT_INSIGHT } from "@/components/dash/MarketInsight";

const clone = (o) => JSON.parse(JSON.stringify(o));
const WATCH_TONES = ["positive", "caution", "neutral"];
const INSIGHT_TONES = [
  { hex: "#0047AB", name: "Blue" }, { hex: "#eda100", name: "Amber" },
  { hex: "#4a3aa7", name: "Violet" }, { hex: "#1baf7a", name: "Green" },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</span>
    {children}
  </label>
);
const inputCls = "w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none";

const FigBlock = ({ title, rows }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="font-num text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">{title}</div>
    <dl className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-3">
          <dt className="text-xs text-slate-500">{k}</dt>
          <dd className="font-num text-sm font-semibold text-slate-900">{v === null || v === undefined || v === "" ? "—" : String(v)}</dd>
        </div>
      ))}
    </dl>
  </div>
);

export function MarketInsightsEditor({ initial, onClose, onSaved }) {
  const fileRef = useRef(null);
  const figRef = useRef(null);
  const [watch, setWatch] = useState(clone(initial?.watch || DEFAULT_WATCH));
  const [insight, setInsight] = useState(clone(initial?.insight || DEFAULT_INSIGHT));
  const [extracting, setExtracting] = useState(false);
  const [figExtracting, setFigExtracting] = useState(false);
  const [figures, setFigures] = useState(null);
  const [saving, setSaving] = useState(false);

  const setCard = (i, k, v) => setWatch((w) => { const c = clone(w); c.cards[i][k] = v; return c; });
  const setStat = (i, k, v) => setInsight((s) => { const c = clone(s); c.stats[i][k] = v; return c; });

  const onExtract = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sc/market-insights/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const dr = data.draft || {};
      if (dr.watch?.cards?.length) setWatch({ ...DEFAULT_WATCH, ...dr.watch, cards: dr.watch.cards.slice(0, 4) });
      if (dr.insight?.stats?.length) setInsight({ ...DEFAULT_INSIGHT, ...dr.insight, stats: dr.insight.stats.slice(0, 4) });
      toast.success("AI draft loaded — review the figures, then Publish.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "AI extraction failed.");
    } finally {
      setExtracting(false);
    }
  };

  const onExtractFigures = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFigExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sc/market-insights/extract-figures", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFigures(data.figures || {});
      toast.success("Figures drafted — review and copy into the current edition.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Figure extraction failed.");
    } finally {
      setFigExtracting(false);
    }
  };

  const onPublish = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/sc/market-insights", { watch, insight });
      toast.success("Market insights published.");
      onSaved?.(data);
      onClose?.();
    } catch {
      toast.error("Could not publish.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="mi-editor">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Edit market insights</h2>
            <p className="text-xs text-slate-500">Upload a market PDF for an AI draft, review, then publish to the home page.</p>
          </div>
          <button onClick={onClose} data-testid="mi-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-8">
          {/* AI upload */}
          <div className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
            <Sparkles className="w-5 h-5 text-primary shrink-0" />
            <div className="text-sm text-slate-600 flex-1">
              <strong>Auto-draft from a PDF</strong> — Alphaliner Monthly Monitor / Newsletter or a Sea-Intelligence report. You review every figure before it goes live.
            </div>
            <button onClick={() => fileRef.current?.click()} disabled={extracting} data-testid="mi-extract-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-primary text-white text-sm font-medium px-3.5 py-2 hover:bg-primary/90 disabled:opacity-60">
              {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
              {extracting ? "Reading…" : "Upload PDF"}
            </button>
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={onExtract} />
          </div>

          {/* AI report-figures draft (idle / reliability / bunker / rates) */}
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3" data-testid="mi-figures-block">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-primary shrink-0" />
              <div className="text-sm text-slate-600 flex-1">
                <strong>Draft report figures from an upload</strong> — idle fleet, schedule reliability, bunker VLSFO and freight rates, pulled from an Alphaliner or Sea-Intelligence PDF for you to copy into the current edition.
              </div>
              <button onClick={() => figRef.current?.click()} disabled={figExtracting} data-testid="mi-figures-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-slate-800 text-white text-sm font-medium px-3.5 py-2 hover:bg-slate-700 disabled:opacity-60">
                {figExtracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                {figExtracting ? "Reading…" : "Upload PDF"}
              </button>
              <input ref={figRef} type="file" accept=".pdf" className="hidden" onChange={onExtractFigures} />
            </div>
            {figures && (
              <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm" data-testid="mi-figures-result">
                <FigBlock title="Idle fleet" rows={[
                  ["Idle %", figures.idle?.pct],
                  ["Ships", figures.idle?.ships],
                  ["TEU", figures.idle?.teu],
                  ["As of", figures.idle?.as_of],
                ]} />
                <FigBlock title="Schedule reliability" rows={[
                  ["Asia→Oceania (HH)", figures.reliability?.asia_oceania_headhaul],
                  ["Oceania→Asia (BH)", figures.reliability?.oceania_asia_backhaul],
                  ["Global", figures.reliability?.global],
                  ["Avg delay (days)", figures.reliability?.avg_delay_days],
                  ["Period", figures.reliability?.period],
                ]} />
                <FigBlock title="Bunker VLSFO (US$/t)" rows={[
                  ["Rotterdam", figures.bunker_vlsfo?.rotterdam],
                  ["Singapore", figures.bunker_vlsfo?.singapore],
                  ["Houston", figures.bunker_vlsfo?.houston],
                  ["Month", figures.bunker_vlsfo?.month],
                ]} />
                <FigBlock title="Freight rates" rows={[
                  ["SHA→SYD", figures.rates?.sha_syd],
                  ["LCH→SYD (Xeneta)", figures.rates?.lch_syd_xeneta],
                  ["SCFI", figures.rates?.scfi],
                  ["As of", figures.rates?.as_of],
                ]} />
                {figures.notes && (
                  <div className="sm:col-span-2 text-xs text-slate-500 leading-relaxed">
                    <span className="font-medium text-slate-600">Notes: </span>{figures.notes}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Alphaliner Watch */}
          <section>
            <h3 className="font-display font-semibold text-slate-900 mb-3">Alphaliner Market Watch (4 cards)</h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <Field label="Week label"><input className={inputCls} value={watch.week_label} onChange={(e) => setWatch({ ...watch, week_label: e.target.value })} /></Field>
              <Field label="Source line"><input className={inputCls} value={watch.source} onChange={(e) => setWatch({ ...watch, source: e.target.value })} /></Field>
            </div>
            <div className="space-y-3">
              {watch.cards.map((c, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`mi-card-${i}`}>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <Field label={`Card ${i + 1} · Value`}><input className={inputCls} value={c.value} onChange={(e) => setCard(i, "value", e.target.value)} /></Field>
                    <Field label="Tag"><input className={inputCls} value={c.tag} onChange={(e) => setCard(i, "tag", e.target.value)} /></Field>
                    <Field label="Tone">
                      <select className={inputCls} value={c.tone} onChange={(e) => setCard(i, "tone", e.target.value)}>
                        {WATCH_TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Description"><textarea rows={2} className={inputCls} value={c.desc} onChange={(e) => setCard(i, "desc", e.target.value)} /></Field>
                </div>
              ))}
            </div>
          </section>

          {/* Market Insight stats */}
          <section>
            <h3 className="font-display font-semibold text-slate-900 mb-3">Market Insight on Services (4 stats)</h3>
            <Field label="Intro paragraph"><textarea rows={3} className={inputCls} value={insight.intro} onChange={(e) => setInsight({ ...insight, intro: e.target.value })} /></Field>
            <div className="mt-3" />
            <div className="space-y-3">
              {insight.stats.map((s, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`mi-stat-${i}`}>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <Field label={`Stat ${i + 1} · Value`}><input className={inputCls} value={s.value} onChange={(e) => setStat(i, "value", e.target.value)} /></Field>
                    <Field label="Label"><input className={inputCls} value={s.label} onChange={(e) => setStat(i, "label", e.target.value)} /></Field>
                    <Field label="Colour">
                      <select className={inputCls} value={s.tone} onChange={(e) => setStat(i, "tone", e.target.value)}>
                        {INSIGHT_TONES.map((t) => <option key={t.hex} value={t.hex}>{t.name}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Sub-text"><textarea rows={2} className={inputCls} value={s.sub} onChange={(e) => setStat(i, "sub", e.target.value)} /></Field>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Field label="Sources line"><input className={inputCls} value={insight.sources} onChange={(e) => setInsight({ ...insight, sources: e.target.value })} /></Field>
            </div>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-b border-slate-100 sticky bottom-0 bg-white rounded-b-2xl border-t">
          <button onClick={onClose} className="rounded-lg border border-slate-200 text-slate-600 text-sm px-4 py-2 hover:bg-slate-50">Cancel</button>
          <button onClick={onPublish} disabled={saving} data-testid="mi-publish-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-medium px-5 py-2 hover:bg-emerald-700 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
